import { useQuery } from '@tanstack/react-query'
import { 
  getLootTableByRarity,
  LootBoxDropTableItemDto,
} from '../../api/loot-boxes/loot-tables'
import { LootBoxRarityEnum } from '../../types/loot-box/loot-box.enums'
import { lootTable } from '../../common/react-query/react-query-keys'
import { inventoryUrl } from '../../common/urls/settingsUrl'
import Link from 'next/link'
import LootBoxDetailCard from './LootBoxDetailCard'

type Props = {
  rarity: LootBoxRarityEnum
  strict?: boolean
}

export default function LootBoxTooltip({
  rarity,
  strict = true,
}: Props) {
  const { data } = useQuery<LootBoxDropTableItemDto[]>(
    [...lootTable, rarity, strict],
    async () => getLootTableByRarity({ rarity, strict }),
    {
      enabled: true, // All visitors should always be able to see loot tables.
      staleTime: 1_000 * 60 * 5, // 5 minutes
      cacheTime: 1_000 * 60 * 5, // 5 minutes
      refetchInterval: 1_000 * 60 * 5, // 5 minutes
      refetchIntervalInBackground: true,
    },
  )

  return (
    <div className='tooltip relative'>
      {data && Array.isArray(data) && (
        <div className='tooltip-content w-72 rounded bg-gray-900 p-4 text-sm shadow-lg'>
          {/* Header */}
          <div className='bg-gradient-to-r from-purple-600/20 to-blue-600/20 mb-2 px-4 py-3 backdrop-blue-sm'>
            <h3 className='text-white font-semibold'>Drop Rates</h3>
          </div>

          {/* Loot Table content */}
          {data.map((i) => (
            <div key={i.id}>
              <div className='flex justify-between'>
                <span className={rarityColor(i.rarity)}>{i.name}</span>
                <span>{(i.probability * 100).toFixed(3)}%</span>
              </div>
              {i.detailCard && (
                <div className="w-full mt-1 mb-1">
                  <LootBoxDetailCard detailCard={i.detailCard} />
                </div>
              )}
            </div>
          ))}

          {/* Footer */}
          <p className="mt-2">
            Items are delivered to your&nbsp;
            <Link href={inventoryUrl} className="underline">
              Inventory
            </Link>
          </p>
        </div>
      )}
    </div>
  )
}

function rarityColor(r: LootBoxRarityEnum) {
  switch (r) {
    case LootBoxRarityEnum.RARE:
      return 'text-blue-400'
    case LootBoxRarityEnum.EPIC:
      return 'text-purple-400'
    case LootBoxRarityEnum.LEGENDARY:
      return 'text-orange-400'
    default:
      return ''
  }
}
